import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Logo from "../../components/logo";
import { Button } from "../../components/ui/button";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../components/ui/select";
import { useAuth, deleteCookie } from "../../contexts/auth-context";
import { useToast } from "../../components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

function Nav({
  selectedPatient,
  setSelectedPatient,
  dropdownOpen,
  setDropdownOpen,
}: {
  selectedPatient: string | null;
  setSelectedPatient: (patient: string) => void;
  dropdownOpen?: boolean; // Optional prop
  setDropdownOpen?: (open: boolean) => void; // Optional prop
}) {
  const { toast } = useToast();
  const { mobile, setMobile } = useAuth();
  const { patients = [] } = usePatientsByMobile({
    selectPatient: setSelectedPatient,
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false); // Internal state for dropdown
  const sidebarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use provided dropdownOpen/setDropdownOpen if available, else use internal state
  const isDropdownOpen = dropdownOpen ?? internalDropdownOpen;
  const toggleDropdownOpen = setDropdownOpen ?? setInternalDropdownOpen;

  function handleSelectPatient(patient: string) {
    setSelectedPatient(patient);
    localStorage.setItem("selectedPatient", patient);
  }

  function handleSignout(event: React.MouseEvent) {
    setMobile(null);
    deleteCookie("authToken");
    setSidebarOpen(false);
    localStorage.removeItem("selectedPatient");
    localStorage.removeItem("mobile");
    toast({
      title: "Signed out",
      description: "You have been signed out successfully",
    });
    navigate("/");
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    }

    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <nav className="flex items-center justify-between">
      <div className="-mr-5 sm:mr-0 sm:mt-4">
        <Logo />
      </div>

      {!!patients.length && (
        <Select
          value={selectedPatient ?? patients[0]?.name}
          onValueChange={handleSelectPatient}
          onOpenChange={toggleDropdownOpen} // Use the toggle function
        >
          <SelectTrigger className="h-8 w-fit gap-2 border-0 p-0">
            <h2 className="text-sm font-bold sm:text-lg truncate">
              <span className="hidden sm:inline">{selectedPatient}</span>
              <span className="sm:hidden">
                {selectedPatient && selectedPatient.replace(/\s/g, '').length > 6
                  ? `${selectedPatient
                      .split('')
                      .reduce((acc, char) => {
                        const noSpaceCount = acc.replace(/\s/g, '').length;
                        return noSpaceCount < 6 ? acc + char : acc;
                      }, '')}...`
                  : selectedPatient}
              </span>
            </h2>
          </SelectTrigger>
          <SelectContent
            className="z-50"
            align="center"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // Stop touch events
            }}
          >
            {patients.map((patient) => (
              <SelectItem key={patient.patientId} value={patient.name}>
                {patient.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className="relative group">
        {!mobile ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="-mr-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.1394 21.6198C17.2594 21.8798 16.2194 21.9998 14.9994 21.9998H8.99937C7.77937 21.9998 6.73937 21.8798 5.85938 21.6198C6.07937 19.0198 8.74937 16.9697 11.9994 16.9697C15.2494 16.9697 17.9194 19.0198 18.1394 21.6198Z"
                    stroke="#292D32"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 2H9C4 2 2 4 2 9V15C2 18.78 3.14 20.85 5.86 21.62C6.08 19.02 8.75 16.97 12 16.97C15.25 16.97 17.92 19.02 18.14 21.62C20.86 20.85 22 18.78 22 15V9C22 4 20 2 15 2ZM12 14.17C10.02 14.17 8.42 12.56 8.42 10.58C8.42 8.60002 10.02 7 12 7C13.98 7 15.58 8.60002 15.58 10.58C15.58 12.56 13.98 14.17 12 14.17Z"
                    stroke="#292D32"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to="/verify-patient?redirect=/doctors">
                <DropdownMenuItem>Sign in</DropdownMenuItem>
              </Link>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            className="-mr-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.1394 21.6198C17.2594 21.8798 16.2194 21.9998 14.9994 21.9998H8.99937C7.77937 21.9998 6.73937 21.8798 5.85938 21.6198C6.07937 19.0198 8.74937 16.9697 11.9994 16.9697C15.2494 16.9697 17.9194 19.0198 18.1394 21.6198Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 2H9C4 2 2 4 2 9V15C2 18.78 3.14 20.85 5.86 21.62C6.08 19.02 8.75 16.97 12 16.97C15.25 16.97 17.92 19.02 18.14 21.62C20.86 20.85 22 18.78 22 15V9C22 4 20 2 15 2ZM12 14.17C10.02 14.17 8.42 12.56 8.42 10.58C8.42 8.60002 10.02 7 12 7C13.98 7 15.58 8.60002 15.58 10.58C15.58 12.56 13.98 14.17 12 14.17Z"
                stroke="#292D32"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        )}
        <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:flex items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg">
          {mobile ? "All Patients" : "Sign in"}
        </span>
      </div>

      {sidebarOpen && mobile && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setSidebarOpen(false)}
          ></div>
          <div
            ref={sidebarRef}
            className="fixed inset-y-0 right-0 w-64 bg-white shadow-lg z-50 flex flex-col p-4"
          >
            <h2 className="text-lg font-bold mb-4">Patients</h2>
            <ul className="flex-grow overflow-y-auto">
              {patients.slice(0, 10).map((patient) => (
                <Link
                  key={patient.patientId}
                  to={`/patient-profile/${patient.patientId}`}
                  className="block p-2 border-b transition-transform duration-200 ease-in-out hover:bg-gray-100 hover:text-blue-600 hover:scale-105"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg text-gray-800">{patient.name}</h3>
                    <span className="text-lg text-gray-400">›</span>
                  </div>
                </Link>
              ))}
              <div
                onClick={() => {
                  navigate(`/add-patient?backUrl=/doctors`);
                }}
                className="block p-2 border-b transition-transform duration-200 ease-in-out cursor-pointer hover:bg-gray-100 hover:text-blue-600 hover:scale-105"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg text-gray-800 font-bold">+ Add New</h3>
                  <span className="text-lg text-gray-400">›</span>
                </div>
              </div>
            </ul>
            <Button onClick={handleSignout} className="mt-4">
              Sign out
            </Button>
          </div>
        </>
      )}
    </nav>
  );
}

export default Nav;